import { logError } from '../state/log-store.js';

const DB_NAME = 'signedge-player';
const DB_VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('manifest')) {
        db.createObjectStore('manifest', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('cache-meta')) {
        const store = db.createObjectStore('cache-meta', { keyPath: 'url' });
        store.createIndex('lastAccessedAt', 'lastAccessedAt', { unique: false });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // Close automatically when a versionchange arrives so deleteDatabase / upgrade
      // requests do not get blocked by lingering connections.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
}

async function withStore(storeName, mode, work) {
  const db = await openDb();
  try {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    return await new Promise((resolve, reject) => {
      let value;
      transaction.oncomplete = () => resolve(value);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
      Promise.resolve(work(store)).then(
        (v) => {
          value = v;
        },
        (err) => reject(err),
      );
    });
  } finally {
    db.close();
  }
}

function req2promise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveManifest(manifest) {
  return withStore('manifest', 'readwrite', (store) =>
    req2promise(store.put({ id: 'current', ...manifest, cachedAt: new Date().toISOString() })),
  );
}

export async function loadManifest() {
  return withStore('manifest', 'readonly', async (store) => {
    const result = await req2promise(store.get('current'));
    return result ?? null;
  });
}

export async function clearManifest() {
  return withStore('manifest', 'readwrite', (store) => req2promise(store.delete('current')));
}

export async function setCacheMeta(url, sizeBytes) {
  return withStore('cache-meta', 'readwrite', (store) => {
    const now = new Date().toISOString();
    return req2promise(store.put({ url, sizeBytes, lastAccessedAt: now, cachedAt: now }));
  });
}

export async function touchCacheMeta(url) {
  return withStore('cache-meta', 'readwrite', async (store) => {
    const item = await req2promise(store.get(url));
    if (!item) return false;
    item.lastAccessedAt = new Date().toISOString();
    await req2promise(store.put(item));
    return true;
  });
}

export async function deleteCacheMeta(url) {
  return withStore('cache-meta', 'readwrite', (store) => req2promise(store.delete(url)));
}

export async function listCacheMetaByLRU() {
  return withStore('cache-meta', 'readonly', (store) => {
    const index = store.index('lastAccessedAt');
    return new Promise((resolve, reject) => {
      const items = [];
      const req = index.openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          items.push(cursor.value);
          cursor.continue();
        } else {
          resolve(items);
        }
      };
      req.onerror = () => reject(req.error);
    });
  });
}

export async function clearAllCacheMeta() {
  return withStore('cache-meta', 'readwrite', (store) => req2promise(store.clear()));
}
